(() => {
  if (customElements.get('yahav-collection-load-more')) return;

  // Keep one reassurance banner after three complete rows at each grid breakpoint.
  // CSS reserves the same row before this script runs and when JavaScript is off.
  function positionTrustBanner(grid) {
    const banner = grid.querySelector(':scope > li.ycol-trust[data-ycol-trust]');
    if (!banner) return;
    const cards = Array.from(grid.querySelectorAll(':scope > .yc-card[data-product-id]'));
    const desktopColumns = Number.parseInt(getComputedStyle(grid.closest('.ycol')).getPropertyValue('--ycol-columns'), 10) || 4;
    const columns = window.innerWidth <= 749 ? 2 : window.innerWidth <= 1199 ? 3 : desktopColumns;
    const target = cards[columns * 3 - 1];
    banner.classList.toggle('ycol-trust--mobile', cards.length >= 6);
    banner.classList.toggle('ycol-trust--tablet', cards.length >= 9);
    banner.classList.toggle('ycol-trust--desktop', cards.length >= desktopColumns * 3);
    banner.hidden = !target;
    if (target && target.nextElementSibling !== banner) target.after(banner);
  }

  const positionTrustBanners = () => document.querySelectorAll('.ycol-grid').forEach(positionTrustBanner);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', positionTrustBanners, { once: true });
  else positionTrustBanners();
  if (typeof window.matchMedia === 'function') {
    ['(max-width: 749px)', '(max-width: 1199px)'].forEach((query) => {
      window.matchMedia(query).addEventListener('change', positionTrustBanners);
    });
  }
  document.addEventListener('shopify:section:load', positionTrustBanners);

  class YahavCollectionLoadMore extends HTMLElement {
    constructor() {
      super();
      this.handleClick = () => this.loadMore();
      this.request = null;
      this.observer = null;
      this.generation = 0;
      this.loadedURLs = new Set();
    }

    connectedCallback() {
      this.root = this.closest('.ycol');
      this.grid = this.root?.querySelector('.ycol-grid');
      this.pagination = this.root?.querySelector('[data-ycol-pagination]');
      this.button = this.querySelector('[data-ycol-load-button]');
      this.status = this.querySelector('[data-ycol-load-status]');
      this.link = this.querySelector('[data-ycol-load-link]');

      if (!this.grid || !this.button || !this.status || !this.link || !this.dataset.nextUrl || !('IntersectionObserver' in window)) return;

      try {
        this.nextURL = this.navigationURL(this.dataset.nextUrl).href;
      } catch (error) {
        return;
      }

      this.buttonLabel = this.buttonLabel || this.button.textContent;
      this.currentPage = Number(this.dataset.currentPage);
      if (!Number.isInteger(this.currentPage) || this.currentPage < 1 || !this.dataset.sectionId) return;

      this.hidden = false;
      if (this.pagination) this.pagination.hidden = true;
      this.button.hidden = false;
      this.button.disabled = false;
      this.button.textContent = this.buttonLabel;
      this.link.href = this.nextURL;
      this.link.hidden = true;
      this.status.textContent = '';
      this.autoPaused = false;
      this.button.addEventListener('click', this.handleClick);
      this.observer = new IntersectionObserver((entries) => {
        if (!this.autoPaused && entries.some((entry) => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '300px 0px' });
      this.observer.observe(this);
    }

    // Keep the current language, filters, sorting and theme preview on each page.
    navigationURL(value) {
      const currentURL = new URL(window.location.href);
      const url = new URL(value, currentURL);
      if (url.origin !== currentURL.origin) throw new Error('Invalid collection page URL');

      new Set(currentURL.searchParams.keys()).forEach((key) => {
        if (key === 'section_id' || key === 'sections' || url.searchParams.has(key)) return;
        currentURL.searchParams.getAll(key).forEach((value) => url.searchParams.append(key, value));
      });
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');
      url.hash = '';
      return url;
    }

    async loadMore() {
      if (!this.isConnected || this.request || !this.nextURL) return;

      const controller = new AbortController();
      const generation = this.generation;
      const requestedURL = this.nextURL;
      this.request = controller;
      this.observer?.unobserve(this);
      this.button.disabled = true;
      this.button.textContent = this.dataset.loadingLabel || 'Loading products…';
      this.link.hidden = true;
      this.status.textContent = this.dataset.loadingLabel || 'Loading products…';
      this.grid.setAttribute('aria-busy', 'true');
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      try {
        if (this.loadedURLs.has(requestedURL)) throw new Error('Collection page already loaded');
        const requestURL = this.navigationURL(requestedURL);
        requestURL.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(requestURL.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection page could not be loaded');
        const html = await response.text();
        if (!this.isConnected || generation !== this.generation) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = documentFragment.getElementById(this.root.id);
        const incomingGrid = incomingRoot?.querySelector('.ycol-grid');
        const incomingLoader = incomingRoot?.querySelector('yahav-collection-load-more');
        const incomingPage = Number(incomingLoader?.dataset.currentPage);
        if (!incomingGrid || !incomingLoader || incomingLoader.dataset.sectionId !== this.dataset.sectionId || !Number.isInteger(incomingPage) || incomingPage !== this.currentPage + 1) {
          throw new Error('Invalid collection page');
        }

        const incomingItems = Array.from(incomingGrid.children);
        const incomingCards = incomingItems.filter((item) => item.matches('.yc-card[data-product-id]'));
        if (!incomingCards.length || incomingItems.some((item) => !item.matches('.yc-card[data-product-id], li.ycol-trust[data-ycol-trust]')) || incomingCards.some((card) => !card.dataset.productId)) {
          throw new Error('Collection page has no valid products');
        }

        const nextURL = incomingLoader.dataset.nextUrl ? this.navigationURL(incomingLoader.dataset.nextUrl).href : '';
        if (nextURL && (nextURL === requestedURL || this.loadedURLs.has(nextURL))) throw new Error('Invalid next collection page');

        const productIDs = new Set(Array.from(this.grid.querySelectorAll(':scope > .yc-card[data-product-id]'), (card) => card.dataset.productId));
        const cards = [];
        const fragment = document.createDocumentFragment();
        incomingCards.forEach((card) => {
          if (productIDs.has(card.dataset.productId)) return;
          productIDs.add(card.dataset.productId);
          const importedCard = document.importNode(card, true);
          cards.push(importedCard);
          fragment.append(importedCard);
        });
        if (!cards.length) throw new Error('Collection page contains no new products');

        const incomingPagination = incomingRoot.querySelector('[data-ycol-pagination]');
        const pagination = incomingPagination ? document.importNode(incomingPagination, true) : null;
        if (pagination) {
          pagination.hidden = true;
          pagination.querySelectorAll('a[href]').forEach((link) => {
            link.href = this.navigationURL(link.getAttribute('href')).href;
          });
        }

        // Only advance the cursor after the complete response has been validated.
        this.grid.append(fragment);
        positionTrustBanner(this.grid);
        this.currentPage = incomingPage;
        this.dataset.currentPage = String(incomingPage);
        this.nextURL = nextURL;
        this.dataset.nextUrl = nextURL;
        this.loadedURLs.add(requestedURL);
        this.autoPaused = false;
        if (this.pagination && pagination) {
          this.pagination.replaceWith(pagination);
          this.pagination = pagination;
        }
        this.root.dispatchEvent(new CustomEvent('ycol:products:added', { bubbles: true, detail: { cards } }));

        this.button.textContent = this.buttonLabel;
        this.button.hidden = !nextURL;
        this.link.hidden = true;
        if (nextURL) {
          this.link.href = nextURL;
          this.status.textContent = this.dataset.loadedLabel || 'More products loaded.';
        } else {
          this.status.textContent = this.dataset.completeLabel || 'You’ve seen all products.';
          this.observer?.disconnect();
        }
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        this.autoPaused = true;
        this.observer?.disconnect();
        this.button.hidden = false;
        this.button.textContent = this.dataset.retryLabel || 'Try again';
        this.link.href = requestedURL;
        this.link.hidden = false;
        this.status.textContent = this.dataset.errorLabel || 'Products could not be loaded. Please try again.';
        return;
      } finally {
        window.clearTimeout(timeout);
        if (generation === this.generation) {
          this.request = null;
          this.button.disabled = false;
          this.grid.removeAttribute('aria-busy');
        }
      }

      if (this.isConnected && generation === this.generation && this.nextURL) this.observer?.observe(this);
    }

    disconnectedCallback() {
      this.generation += 1;
      this.autoPaused = true;
      this.observer?.disconnect();
      this.observer = null;
      this.request?.abort();
      this.request = null;
      this.button?.removeEventListener('click', this.handleClick);
      this.grid?.removeAttribute('aria-busy');
    }
  }

  customElements.define('yahav-collection-load-more', YahavCollectionLoadMore);
})();
